import React from 'react';
import './tailwind-components.css';

type ChildrenProps = {
  children: React.ReactNode;
};

type IdChildrenProps = {
  children: React.ReactNode;
  id?: string;
};

type TimelineStyleType = 'job' | 'paid' | 'partial' | 'wait';

type TimelineBarProps = {
  left: number | string;
  width: number | string;
  top: number;
  styleType: TimelineStyleType;
  label?: string;
  dateRange?: string;
  barId?: string;
  relatedToId?: string;
  barTestId?: string;
};

type LegendSwatchProps = {
  swatchType: TimelineStyleType;
};

type TimelineGridProps = {
  children: React.ReactNode;
  canvasWidthPercent?: number;
  canvasHeightPx?: number;
  weekCount?: number;
};

type WeekRowProps = {
  children: React.ReactNode;
  weekCount?: number;
  leadingBufferPx?: number;
  trailingBufferPx?: number;
};

const timelineStyleMap: Record<TimelineStyleType, React.CSSProperties> = {
  job: { background: '#005293' },
  paid: { background: '#8cc442' },
  partial: {
    background:
      'repeating-linear-gradient(45deg, #8cc442, #8cc442 6px, #d9ecbc 6px, #d9ecbc 12px)',
    border: '1px solid #8cc442',
  },
  wait: {
    background: '#f2f2f2',
    border: '1px solid #4e4e4e',
  },
};

export const PageWrapper = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-flex-col tw-gap-5 max-[639px]:tw-gap-0">
    {children}
  </div>
);

export const IntroCard = ({ children, id }: IdChildrenProps) => (
  <div
    id={id}
    className="tw-bg-white tw-rounded-[12px] tw-shadow-[0px_2px_8px_rgba(2,84,126,0.14)] tw-text-unm-adhoc2 tw-font-sans tw-text-sm tw-leading-5 tw-px-7 tw-py-6 max-[639px]:tw-rounded-none max-[639px]:tw-px-6 max-[639px]:tw-py-5"
  >
    {children}
  </div>
);

export const Container = ({ children, id }: IdChildrenProps) => (
  <div
    id={id}
    className="tw-bg-white tw-rounded-[12px] tw-shadow-[0px_2px_8px_rgba(2,84,126,0.14)] tw-p-5 tw-w-full max-[639px]:tw-rounded-none max-[639px]:tw-p-6 tw-pb-8"
  >
    {children}
  </div>
);

export const HeaderRow = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-[14px]">
    {children}
  </div>
);

export const Header = ({ children }: ChildrenProps) => (
  <h1 className="tw-text-unm-adhoc2 tw-font-sans tw-text-2xl tw-leading-8 tw-font-normal tw-m-0 max-[1180px]:tw-text-[20px] max-[1180px]:tw-leading-6 max-[639px]:tw-text-[20px] max-[639px]:tw-leading-6">
    {children}
  </h1>
);

export const IconButtonSingle = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'>
>(({ className = '', children, ...props }, ref) => (
  <button
    {...props}
    ref={ref}
    type={props.type ?? 'button'}
    className={`tw-bg-white tw-border tw-border-solid tw-border-unm-border1 tw-rounded tw-text-[#127CB7] tw-font-sans tw-text-sm tw-font-semibold tw-h-[30px] tw-leading-[14px] tw-m-0 tw-ml-auto tw-max-w-[114px] tw-w-full max-[576px]:tw-max-w-[138px] disabled:tw-bg-unm-bg-light disabled:tw-text-unm-adhoc2 ${className}`.trim()}
  >
    {children}
  </button>
));

IconButtonSingle.displayName = 'IconButtonSingle';

export const MetricsRow = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-flex-wrap tw-gap-y-2 tw-gap-x-6 tw-mb-4 max-[639px]:tw-gap-y-[10px] max-[639px]:tw-gap-x-[14px]">
    {children}
  </div>
);

export const Metric = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-items-baseline tw-gap-2 tw-text-unm-adhoc2 tw-font-sans tw-text-base tw-leading-6 max-[1180px]:tw-text-sm max-[1180px]:tw-leading-5">
    {children}
  </div>
);

export const MetricLabel = ({ children }: ChildrenProps) => (
  <span className="tw-font-normal">{children}</span>
);

export const MetricValue = ({ children }: ChildrenProps) => (
  <span className="tw-font-semibold">{children}</span>
);

export const CalendarShell = ({ children }: ChildrenProps) => (
  <div className="tw-border tw-border-[#bbbbbb] tw-rounded-[12px] tw-overflow-hidden max-[639px]:tw-p-3">
    {children}
  </div>
);

export const TimelineHeader = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-items-baseline tw-justify-between tw-mb-2">
    {children}
  </div>
);

export const TimelineAnchor = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-items-center tw-gap-1 tw-text-[#222222] tw-font-sans tw-text-sm tw-leading-5 [&_strong]:tw-font-semibold">
    {children}
  </div>
);

export const Dot = () => (
  <span className="tw-inline-block tw-h-2 tw-w-2 tw-rounded-full tw-bg-[#4e4e4e]" />
);

type TimelineMarkerProps = {
  left: number | string;
  date: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  reverseLabel?: boolean;
};

const markerLabelTransform: Record<
  NonNullable<TimelineMarkerProps['align']>,
  string
> = {
  left: 'translateX(0)',
  center: 'translateX(-50%)',
  right: 'translateX(-100%)',
};

export const TimelineMarker = ({
  left,
  date,
  label,
  align = 'center',
  reverseLabel = false,
}: TimelineMarkerProps) => (
  <div
    className="tw-hidden min-[640px]:tw-block tw-absolute tw-top-0 tw-bottom-0 tw-z-[3] tw-pointer-events-none"
    style={{ left: typeof left === 'number' ? `${left}%` : left }}
  >
    <div
      className="tw-absolute tw-top-0 tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-bg-white/90 tw-px-1 tw-pt-3 tw-text-[#222222] tw-font-sans tw-text-xs tw-leading-4 [&_strong]:tw-font-semibold"
      style={{ transform: markerLabelTransform[align] }}
    >
      {reverseLabel ? (
        <>
          <strong>{label}</strong>
          <span>{date}</span>
          <Dot />
        </>
      ) : (
        <>
          <Dot />
          <span>{date}</span>
          <strong>{label}</strong>
        </>
      )}
    </div>
    <div className="tw-absolute tw-top-5 tw-bottom-0 tw-left-0 tw-w-px tw-border-lv tw-border-[#9aa3af]" />
  </div>
);

export const TimelineGrid = ({
  children,
  canvasWidthPercent = 100,
  canvasHeightPx = 520,
  weekCount = 12,
}: TimelineGridProps) => {
  const weeks = Math.max(1, weekCount);
  const weekWidth = `calc(100% / ${weeks})`;

  return (
    <div className="leave-plan-timeline-scroll tw-relative tw-h-[520px] tw-overflow-auto tw-border tw-border-solid tw-border-[#bbbbbb] tw-rounded-[10px]">
      <div
        className="tw-relative"
        style={{
          ['--leave-plan-grid-height' as string]: '520px',
          width: `${Math.max(100, canvasWidthPercent)}%`,
          minHeight: `${Math.max(520, canvasHeightPx)}px`,
        }}
      >
        <div
          aria-hidden="true"
          className="tw-absolute tw-inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent calc(${weekWidth} - 1px), #bbbbbb calc(${weekWidth} - 1px), #bbbbbb ${weekWidth})`,
          }}
        />
        {children}
      </div>
    </div>
  );
};

export const WeekRow = ({
  children,
  weekCount = 12,
  leadingBufferPx = 0,
  trailingBufferPx = 0,
}: WeekRowProps) => (
  <div
    className="leave-plan-week-row tw-grid tw-items-center tw-text-[#4e4e4e] tw-font-sans tw-text-xs tw-leading-4 tw-px-2 tw-py-2"
    style={{
      gridTemplateColumns: `${Math.max(0, leadingBufferPx)}px repeat(${Math.max(
        1,
        weekCount,
      )}, minmax(0, 1fr)) ${Math.max(0, trailingBufferPx)}px`,
    }}
  >
    <div aria-hidden="true" />
    {children}
    <div aria-hidden="true" />
  </div>
);

export const WeekCell = ({ children }: { children?: React.ReactNode }) => (
  <div className="tw-text-center">{children}</div>
);

export const TimelineBar = ({
  left,
  width,
  top,
  styleType,
  label,
  dateRange,
  barId,
  relatedToId,
  barTestId,
}: TimelineBarProps) => (
  <>
    <div
      data-testid={barTestId}
      data-bar-id={barId}
      data-related-to={relatedToId}
      data-style-type={styleType}
      className="tw-absolute tw-z-[2] tw-h-5 tw-rounded-[20px]"
      style={{
        left: typeof left === 'number' ? `${left}%` : left,
        width: typeof width === 'number' ? `${width}%` : width,
        top: `${top}px`,
        ...timelineStyleMap[styleType],
      }}
    />
    {label && (
      <div
        className="tw-hidden min-[640px]:tw-flex tw-flex-col tw-absolute tw-z-[3] tw-mt-1 tw-mb-2 tw-pointer-events-none"
        style={{
          left: typeof left === 'number' ? `${left}%` : left,
          top: `${top + 22}px`,
          maxWidth:
            typeof left === 'number'
              ? `calc(${Math.max(0, 100 - left)}% - 8px)`
              : 'calc(100% - 8px)',
        }}
      >
        <span className="tw-text-[#222222] tw-font-sans tw-font-semibold tw-text-xs tw-leading-4 tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
          {label}
        </span>
        {dateRange && (
          <span className="tw-text-[#4e4e4e] tw-font-sans tw-text-[11px] tw-leading-4 tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
            {dateRange}
          </span>
        )}
      </div>
    )}
  </>
);

export const TimelineLegend = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-flex-wrap tw-gap-y-2 tw-gap-x-[18px] tw-mt-[14px]">
    {children}
  </div>
);

export const TimelineLegendItem = ({ children }: ChildrenProps) => (
  <div className="tw-inline-flex tw-items-center tw-gap-2 tw-text-unm-statusflag-text tw-font-sans tw-font-semibold tw-text-xs tw-leading-4">
    {children}
  </div>
);

export const TimelineLegendSwatch = ({ swatchType }: LegendSwatchProps) => (
  <span
    className="tw-inline-block tw-h-3 tw-w-6 tw-rounded-full"
    style={timelineStyleMap[swatchType]}
  />
);

export const ActionsContainer = ({ children }: ChildrenProps) => (
  <div className="tw-mt-5 max-[639px]:tw-mt-4">{children}</div>
);

export const DesktopActions = ({ children }: ChildrenProps) => (
  <div className="tw-hidden min-[640px]:tw-flex tw-items-center tw-justify-end tw-gap-3">
    {children}
  </div>
);

export const MobileActions = ({ children }: ChildrenProps) => (
  <div className="tw-flex tw-flex-col tw-gap-3 min-[640px]:tw-hidden">
    {children}
  </div>
);

export const MobileTimelineList = ({ children, id }: IdChildrenProps) => (
  <div
    id={id}
    className="tw-mt-4 tw-flex tw-flex-col tw-gap-4 min-[640px]:tw-hidden"
  >
    {children}
  </div>
);

type MobileTimelineItemProps = ChildrenProps & {
  className?: string;
};

export const MobileTimelineItem = ({
  children,
  className = '',
}: MobileTimelineItemProps) => (
  <div
    className={`tw-flex tw-flex-col tw-gap-2 tw-py-1 tw-w-[40px] tw-shrink-0 ${className}`.trim()}
  >
    {children}
  </div>
);

type MobileTimelineTrackProps = React.HTMLAttributes<HTMLDivElement> & {
  swatchType: TimelineStyleType;
  height: number;
  letter: string;
};

export const MobileTimelineTrack = ({
  swatchType,
  height,
  letter,
  className = '',
  style,
  ...props
}: MobileTimelineTrackProps) => (
  <div
    {...props}
    className={`tw-flex tw-w-[40px] tw-items-center tw-justify-center tw-rounded-[18px] tw-font-sans tw-font-semibold tw-text-white tw-shadow-[0px_1px_2px_rgba(0,0,0,0.08)] ${className}`.trim()}
    style={{
      height: `${height}px`,
      ...timelineStyleMap[swatchType],
      ...style,
    }}
  >
    <span className="tw-inline-flex tw-h-6 tw-min-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#767676] tw-text-xs">
      {letter}
    </span>
  </div>
);

export const MobileTimelineBadge = ({ children }: ChildrenProps) => (
  <div className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-5 tw-leading-5 tw-px-[6px] tw-rounded-full tw-bg-[#767676] tw-text-white tw-font-sans tw-font-semibold tw-text-xs">
    {children}
  </div>
);

type MobileTimelineTextProps = ChildrenProps & {
  className?: string;
};

export const MobileTimelineText = ({
  children,
  className = '',
}: MobileTimelineTextProps) => (
  <div
    className={`tw-flex tw-flex-col tw-text-[#222222] tw-font-sans tw-text-xs tw-leading-4 ${className}`.trim()}
  >
    {children}
  </div>
);

export const MobileTimelineTitle = ({ children }: ChildrenProps) => (
  <span className="tw-font-semibold">{children}</span>
);

export const MobileTimelineDate = ({ children }: ChildrenProps) => (
  <span className="tw-text-[#4e4e4e]">{children}</span>
);

export const PopupContent = ({ children, id }: IdChildrenProps) => (
  <div
    id={id}
    className="tw-flex tw-flex-col tw-py-[6px] tw-px-4 [&_a]:tw-text-unm-primaryblue"
  >
    {children}
  </div>
);
